/**
 * Copy e dados do evento — espelho de docs/briefing.md.
 *
 * POR QUE ISTO EXISTE, se a página é editável no Builder.io:
 *
 * Cada valor aqui vira o `defaultValue` do input correspondente no
 * builder-registry. Com isso:
 *   1. a página renderiza completa ANTES de existir space no Builder — o build
 *      não depende de rede nem de chave para produzir HTML correto;
 *   2. quem abre o editor encontra os campos já preenchidos com a copy
 *      aprovada, em vez de caixas vazias;
 *   3. o texto aprovado tem UM lugar só. Espalhar a copy pelos JSX faria a
 *      conferência contra o briefing virar caça ao tesouro.
 *
 * Ao alterar copy aqui, conferir docs/briefing.md — ele é a fonte de verdade
 * do cliente, este arquivo é a transcrição executável.
 */

export const EVENTO = {
  nome: 'Rota da Eficiência',
  realizador: 'Apresenta.me',
  /* `heroChamada` fecha o hero, depois do CTA — decisão de 07/08. Não é mais o
     que o nome sugere ("chamada" de abertura): a posição mudou de entre o H1 e
     a tagline para depois do botão, como assinatura em caixa alta. Mantido o
     nome porque o dado em si — o gancho de tensão — não mudou, só onde
     renderiza (ver .hero-fecho em componentes.css). */
  heroChamada: 'O próximo nível da sua operação no mercado imobiliário exige uma nova direção',
  tagline: 'O caminho para escalar a sua imobiliária com eficiência',
  /* Trecho da tagline que sai em negrito no hero. Fica FORA da string acima de
     propósito: `tagline` também alimenta o <meta name="description"> e o
     og:description, onde uma tag <strong> apareceria como texto literal no
     resultado de busca. O hero recorta o trecho na hora de renderizar; se
     alguém reescrever a tagline no Builder e este trecho deixar de existir
     nela, a frase sai inteira sem negrito, sem quebrar. */
  taglineDestaque: 'a sua imobiliária',
  /* Saudação que abre o lockup do H1: "Bem-vindo à" + logo "Rota da
     Eficiência". É a abertura literal do fundo de palco e do totem de
     credenciamento — quem chega no evento lê exatamente esta frase na porta.
     Esvaziar aqui (ou no Builder) devolve o título sozinho, sem quebrar nada. */
  heroSaudacao: 'Bem-vindo à',
  /* Promessa do dia inteiro — pedida pelo cliente na revisão da 1ª dobra
     ("Adicionar uma frase dizendo que o evento será o dia todo"). Fica FORA da
     tagline porque tem outro trabalho: a tagline posiciona o evento, esta
     frase diz o que a pessoa recebe em troca do dia dela. */
  heroPromessa:
    'Um dia inteiro sobre vendas, locação, processos e o que você precisa para crescer a sua imobiliária.',
  /* Carimbo de edição. O evento é itinerante — "Rota da Eficiência" é a série,
     "Edição Blumenau" é esta parada. Aparece na camisa, no crachá, na sacola e
     no fundo de palco. */
  edicao: 'Blumenau',
  /* GRATUIDADE — a informação que mais move a decisão e que não estava em
     lugar nenhum da página. O ingresso não custa dinheiro; custa um dia de
     agenda. Por isso as duas frases andam juntas em toda a página: dizer
     "gratuito" sozinho levanta a pergunta "qual é a pegadinha?", e a resposta
     honesta é o compromisso de ficar o dia todo. */
  seloGratuito: 'Entrada gratuita',
  compromisso: 'Sem custo de ingresso. O que se pede é o compromisso de estar presente o dia inteiro.',
  /* Lotação. Aparece no hero como quarta linha da ficha e na dobra da
     gratuidade; o número em si já vivia na frase de GRATUIDADE.resposta, e
     agora tem um campo próprio para não obrigar quem edita a mexer numa frase
     longa para corrigir um número. */
  vagas: 'Vagas limitadas a 150 participantes',
  /* Fundo do hero. Aponta para a versão web (2560px, 788 KB) e não para a
     bnu-back.jpg original (4000px, 1,8 MB): este arquivo é o LCP da página, e
     nenhuma tela precisa de 4000px. A original fica no repositório como mestre
     para futuros recortes. Trocar aqui exige refazer a medição do véu — ver
     .hero-com-imagem em componentes.css. */
  heroFundo: '/img/bnu-back-web.jpg',
  /* Mascote "Nauta" no hero — pose da BANDEIRA (nauta-rota-2). Trocado em
     11/08 do `nauta-joia-web.png`: aquele vem de outra família de arte (traço
     chapado, capacete fosco) e o kit do evento inteiro — fundo de palco,
     totem, camisa, sacola, deck de patrocínio — abre com esta pose, a do
     astronauta fincando a bandeira. É literalmente a imagem que a pessoa vê ao
     entrar no evento; era o que mais destoava entre a landing e as peças.

     O <img> só renderiza se este campo tiver valor, então esvaziar aqui (ou no
     Builder) devolve a cena sem o mascote, sem quebrar nada.

     Aponta para a versão `-web.png` (420×631, 88 KB), não para o original
     (1024×1536, 1,7 MB): recorte pelo bounding box do alfa e QUANTIZAÇÃO para
     64 cores SEM dither — a arte é vetorial chapada, e a difusão de erro é
     justamente o que impede o PNG de comprimir. Os originais ficam na mesma
     pasta como mestres. */
  heroMascote: '/img/nauta-rota-2-web.png',
  /* Pose "bandeira azul + joinha" (nauta-rota-3), no estado de sucesso da
     inscrição — é a pose oficial do kit para validação/prova social (ver a
     tabela de poses em nauta-rota-{1..5} no briefing de identidade). Trocado
     de `nauta-comemora-web.png` (braços erguidos, fora da família oficial)
     em 11/08, junto da migração das outras 4 dobras para o kit "Nauta".

     O fallback do `onError` em SecaoInscricao.jsx (que cai no anel com
     tique) continua valendo por qualquer motivo de falha de carregamento —
     ver o histórico completo desse mecanismo no comentário lá. */
  sucessoMascote: '/img/nauta-rota-3-web.png',
  /* DUAS FOTOS PEDIDAS NO PDF DE 12/08 QUE NÃO VIERAM NO MATERIAL.
     Ficam como campo vazio de propósito: cada consumidor só renderiza a
     imagem se houver caminho, então a página fecha sem buraco enquanto os
     arquivos não chegam, e passa a mostrá-los no minuto em que alguém
     preencher aqui (ou no Builder).

       almocoFoto  o prato servido no almoço, ao lado do formulário
                   ("adicionar o prato de comida")
       localFoto   a fachada do Grupo RVG Rivage, na dobra do local
                   ("trazer a foto do local do evento real também")

     A foto da fachada existe DENTRO do PDF, mas PDF não é fonte de arquivo:
     o que dá para extrair dali é uma reamostragem de tela, e a dobra do local
     mostra a imagem em ~600px de largura. */
  almocoFoto: '/img/prato-almoco-web.webp',
  localFoto: '/img/local-rvg-rivage-web.webp',
  dataLegivel: '22 de setembro',
  cidade: 'Blumenau (SC)',
  horario: 'Presencial das 8h às 19h',
  /* ISO com offset explícito. -03:00 é o horário de Brasília em setembro de
     2026; o horário de verão não existe no Brasil desde 2019. Sem o offset, o
     contador erraria por 3 horas para quem abrisse a página fora do fuso. */
  dataISO: '2026-09-22T08:00:00-03:00',
  local: 'Grupo RVG Rivage',
  endereco: 'Rua 25 de Julho, 907, Itoupava Norte, Blumenau - SC, 89053-000',
  siteInstitucional: 'https://apresenta.me',
}

/** Destino de TODOS os CTAs da página. Diretriz do briefing: âncora interna. */
export const ANCORA_INSCRICAO = '#inscricao'

/**
 * Navegação do cabeçalho.
 *
 * SEIS ITENS, e não as dez dobras que a página tem. O rodapé lista todas; o
 * topo lista as que respondem à pergunta de quem ainda não decidiu — o que
 * vou ver, quem fala, o que faço lá, onde é, quem organiza, quem apoia. As
 * dobras de gratuidade, público e inscrição ficam de fora porque já são o
 * destino de todos os CTAs da página, e repetir o mesmo destino no menu gasta
 * a barra sem levar ninguém a lugar nenhum.
 *
 * `curto` é o rótulo usado quando a barra aperta (1024–1200px), antes de o
 * menu virar disclosure. Sem ele, "Sobre a Apresenta.me" quebra em duas linhas
 * e desalinha a barra inteira.
 */
/**
 * Os seis assuntos do dia, como etiquetas do hero.
 *
 * NÃO SÃO os seis temas do palco (PALCO) nem as quatro rodas do Apre.Lab: são
 * a versão de uma palavra de cada assunto, para quem bate o olho na primeira
 * tela e decide em dois segundos se aquilo é sobre o problema dele. A lista
 * longa vive na dobra do palco, onde há espaço para explicar.
 *
 * `icone` referencia src/lib/icones.jsx pelo nome. Nome desconhecido cai no
 * ícone de alvo em vez de derrubar a dobra.
 */
/* `texto` acompanha cada assunto desde 12/08: na arte aprovada a faixa dos
   seis é um bloco de cartões da largura da página, com uma linha de
   explicação sob cada nome. Campo opcional — sem ele o cartão fecha no nome,
   como era antes. */
export const TEMAS_HERO = [
  {
    rotulo: 'Mercado',
    icone: 'barras',
    texto: 'Tendências e dados que orientam decisões e geram resultados.',
  },
  {
    rotulo: 'IA',
    icone: 'raio',
    texto: 'Inteligência Artificial aplicada à rotina imobiliária.',
  },
  {
    rotulo: 'Marketing',
    icone: 'megafone',
    texto: 'Estratégias modernas para atrair, engajar e converter mais.',
  },
  {
    rotulo: 'Locação',
    icone: 'chave',
    texto: 'Processos e práticas para acelerar locações e reduzir vacância.',
  },
  {
    rotulo: 'Processos',
    icone: 'engrenagem',
    texto: 'Rotinas eficientes que padronizam e aumentam a produtividade.',
  },
  {
    rotulo: 'Gestão',
    icone: 'alvo',
    texto: 'Liderança, métricas e gestão para crescer com consistência.',
  },
]

export const NAVEGACAO = [
  { rotulo: 'O evento',             curto: 'O evento',    destino: '#palco' },
  { rotulo: 'Palestrantes',         curto: 'Palestrantes', destino: '#palestrantes' },
  { rotulo: 'Apre.Lab',             curto: 'Apre.Lab',    destino: '#aprelab' },
  { rotulo: 'Local',                curto: 'Local',       destino: '#local' },
  { rotulo: 'Sobre a Apresenta.me', curto: 'Sobre',       destino: '#sobre' },
  { rotulo: 'Patrocinadores',       curto: 'Apoio',       destino: '#patrocinadores' },
]

/**
 * Assinatura da Rota — as quatro promessas do evento, na ordem em que aparecem
 * em TODAS as peças físicas (fundo de palco, totem, display de mesa, sacola,
 * crachá, caderno) e em todas as lâminas do deck de patrocínio.
 *
 * A ordem não é decorativa, é uma escada: tira o atrito, o que sobra é clareza;
 * com clareza vem eficiência; eficiência vira resultado. Reordenar quebra a
 * frase.
 *
 * `icone` referencia src/lib/icones.jsx pelo nome; nome desconhecido cai no
 * alvo em vez de derrubar a faixa (ver AssinaturaRota.jsx).
 */
/**
 * AS PARADAS DA ROTA — a espinha da página.
 *
 * Não é o mesmo dado de `NAVEGACAO`, e a diferença é deliberada. O menu do topo
 * lista os seis assuntos que alguém PROCURA ("Palestrantes", "Local"). A
 * espinha lista as oito paradas do percurso, na ordem em que a página as
 * apresenta, incluindo dobras que não fazem sentido no menu mas fazem no
 * trajeto (a abertura, a inscrição). Misturar os dois obrigaria um dos dois a
 * mentir sobre o que é.
 *
 * O rótulo aqui é CURTO de propósito: ele vive num trilho de ~170px, ao lado do
 * conteúdo, e nome longo quebraria em duas linhas por parada.
 *
 * ⚠️ Os `destino` têm que existir como `id` no DOM. Se um deles não casar, a
 * parada some do trilho em silêncio (EspinhaRota filtra o que não encontra) em
 * vez de derrubar a página.
 */
export const PARADAS_ROTA = [
  { rotulo: 'A rota',        destino: '#hero' },
  { rotulo: 'No palco',      destino: '#palco' },
  { rotulo: 'Quem fala',     destino: '#palestrantes' },
  { rotulo: 'Para quem é',   destino: '#para-quem' },
  { rotulo: 'Apre.Lab',      destino: '#aprelab' },
  { rotulo: 'Quanto custa',  destino: '#gratuito' },
  { rotulo: 'Inscrição',     destino: '#inscricao' },
  { rotulo: 'Onde acontece', destino: '#local' },
]

export const ASSINATURA_ROTA = [
  { texto: 'Menos atrito.', icone: 'alvo' },
  { texto: 'Mais clareza.', icone: 'conversa' },
  { texto: 'Mais eficiência.', icone: 'calculadora' },
  { texto: 'Melhores resultados.', icone: 'barras' },
]

/* `icone` referencia um dos componentes em ICONES_PALCO (SecaoPalco.jsx) pelo
   nome — os mesmos nomes do Tabler Icons (tabler.io/icons, MIT), de onde os
   paths foram copiados. Tema sem `icone` reconhecido cai no ícone padrão
   (bulb) em vez de quebrar a seção — ver iconePara() em SecaoPalco.jsx.

   `imagem` (opcional) vira foto de fundo do tile no bento, com duotone e véu
   por cima (ver .palco-tile-foto em componentes.css). Preenchida só nos dois
   temas dos tiles LARGOS (1º e último) — foto nos seis viraria galeria e
   pesaria a seção. As duas atuais vêm do Unsplash (licença Unsplash: uso
   comercial livre, sem atribuição obrigatória), tratadas para 1200×560. */
// ── 2ª dobra ────────────────────────────────────────────────────────────────
export const TEMAS_PALCO = [
  {
    titulo: 'Panorama do Mercado',
    texto: 'Para onde o mercado imobiliário está indo e o que isso significa para quem opera hoje',
    icone: 'chart-line',
    imagem: '/img/palco-panorama.jpg',
  },
  {
    titulo: 'Inteligência Artificial',
    texto: 'O que já está funcionando, o que ainda é promessa e como usar IA para ganhar eficiência',
    icone: 'sparkles',
  },
  {
    titulo: 'Reforma Tributária',
    texto: 'As mudanças já estão chegando e quem entende as regras consegue se posicionar melhor',
    icone: 'gavel',
  },
  {
    titulo: 'Marketing Imobiliário',
    /* "extrair máximo resultado para sua receita" → faltavam dois artigos. */
    texto: 'Formas de gerar demanda qualificada e extrair o máximo de resultado para a sua receita',
    icone: 'speakerphone',
  },
  {
    titulo: 'Tecnologia Eficiente',
    texto: 'Processos, ferramentas e tecnologia que oferecem mais eficiência na operação',
    icone: 'settings',
  },
  {
    /* Era "Network Qualificado", com o corpo do cartão dizendo "Networking
       qualificado" duas linhas abaixo. Em português "network" é a rede;
       "networking" é a prática — e é a prática que o cartão descreve. */
    titulo: 'Networking Qualificado',
    texto: 'Networking qualificado com líderes imobiliários e profissionais do mercado',
    icone: 'users-group',
    imagem: '/img/palco-network.jpg',
  },
]

// ── 3ª dobra ────────────────────────────────────────────────────────────────
// `foto` aponta para a versão `-retrato.jpg` (640×800, 4:5, ~40-65KB), não
// para o PNG original entregue (1000×1000, ~700KB-1,1MB): o retrato renderiza
// a ~280px de largura em 4 colunas, então 640px cobre retina 2x com folga. O
// corte 4:5 apara só as laterais do quadrado original — a altura (onde está o
// rosto) fica inteira. O PNG fica em site/static/img como mestre, para o dia
// em que a foto precisar de outro recorte; a versão `-web.jpg` (480×480,
// recorte do círculo antigo) também segue lá.
export const PALESTRANTES = [
  {
    nome: 'Dioner Segala',
    cargo: 'Sócio e Diretor de Consultoria da CUPOLA',
    palestra: 'Panorama do Mercado Imobiliário Regional',
    descricao: 'Dados, tendências e estratégias para crescer com mais eficiência',
    foto: '/img/dioner-segala-retrato.jpg',
    /* Ícone do TEMA que a pessoa leva ao palco, não dela — ver ICONES_TEMA
       em SecaoPalestrantes.jsx. */
    icone: 'barras',
  },
  {
    nome: 'Marcos Alexandre Claudino',
    cargo: 'Sócio Fundador da Claudino Advogados',
    palestra: 'Reforma Tributária e os impactos no Setor Imobiliário',
    descricao: 'O Novo Cenário Tributário no Mercado de Imóveis',
    foto: '/img/marcos-alexandre-claudino-retrato.jpg',
    /* Ícone do TEMA que a pessoa leva ao palco, não dela — ver ICONES_TEMA
       em SecaoPalestrantes.jsx. */
    icone: 'balanca',
  },
  {
    nome: 'Helena Hermes',
    cargo: 'Líder de Growth, Marketing e CMO da Apresenta.me',
    palestra: 'Marketing Imobiliário sem desperdício',
    /* Mesmo ajuste de artigo do tema equivalente em TEMAS_PALCO. A frase veio
       assim do cliente; o sentido não muda. */
    descricao: 'Formas de gerar demanda qualificada e extrair o máximo de resultado',
    foto: '/img/helena-hermes-retrato.jpg',
    /* Ícone do TEMA que a pessoa leva ao palco, não dela — ver ICONES_TEMA
       em SecaoPalestrantes.jsx. */
    icone: 'megafone',
  },
  {
    nome: 'Diego Bonastre',
    cargo: 'Gerente de Relacionamento com o Mercado na Lastro/Lais',
    palestra: 'Imobiliária 3.0',
    descricao: 'Como IA e gestão de dados impulsionam os seus resultados',
    foto: '/img/diego-bonastre-retrato.jpg',
    /* Ícone do TEMA que a pessoa leva ao palco, não dela — ver ICONES_TEMA
       em SecaoPalestrantes.jsx. */
    icone: 'faisca',
  },
]

// ── 4ª dobra ────────────────────────────────────────────────────────────────
/* `avatar` é o recorte do busto do Nauta, um por perfil — gerados por
   scripts/gerar-avatares-nauta.py a partir dos masters, com fundo
   transparente. As três poses são diferentes de propósito: os displays de
   mesa do evento fazem o mesmo (um Nauta por mesa), e três recortes idênticos
   lado a lado leriam como erro de montagem.

   Campo opcional — sem ele o cartão renderiza só com o marco numerado, como
   era antes. */
export const PUBLICO_ALVO = [
  {
    titulo: 'Donos e Sócios',
    texto: 'Que entendem que visão de mercado não pode ficar presa em uma cabeça só',
    avatar: '/img/nauta-bandeira-avatar-web.png',
  },
  {
    titulo: 'Diretores e Gestores',
    texto: 'Que atuam na estratégia da imobiliária em locação, vendas, atendimento e financeiro',
    avatar: '/img/nauta-plataforma-avatar-web.png',
  },
  {
    titulo: 'Imobiliárias em crescimento',
    texto: 'Que precisam entender de que forma podem ser mais eficientes no cotidiano',
    avatar: '/img/nauta-joinha-avatar-web.png',
  },
]

/* Dobra "quanto custa" — pedida pelo cliente ("deveria ter mais seções
   dizendo que o evento é gratuito"). Não é um selo repetido: é a dobra que
   troca a pergunta de preço pela pergunta de agenda, que é a objeção real de
   um diretor de imobiliária.

   Os três itens são o que a pessoa recebe em troca do dia — nesta ordem,
   porque é a ordem em que o dia acontece. */
export const GRATUIDADE = {
  chamada: 'Quanto custa participar?',
  resposta: 'Nada.',
  texto:
    'O ingresso é gratuito e as vagas são limitadas a 150. O único compromisso é ' +
    'aparecer e ficar o dia inteiro, porque é o dia inteiro que faz a diferença ' +
    'entre assistir a um evento e sair dele com decisões tomadas.',
  itens: [
    {
      titulo: 'Um dia inteiro, das 8h às 19h',
      texto: 'Conteúdo de mercado pela manhã, prática à tarde. Café e networking incluídos.',
    },
    {
      titulo: 'Marketing e IA aplicados à sua rotina',
      texto: 'O que já funciona hoje na operação de uma imobiliária. Não tendência, não promessa.',
    },
    {
      titulo: 'Você sai com decisões, não com anotações',
      texto: 'No Apre.Lab, cada participante mapeia os gargalos da própria operação e define os próximos passos.',
    },
  ],
  observacao: 'O almoço no local é opcional, por adesão, e cobrado no dia.',
}

// ── dobra Apre.Lab (nova, entre "para quem é" e a inscrição) ────────────────
/**
 * As quatro Rodas de Eficiência.
 *
 * ⚠️ AS CORES SÃO A ÚNICA EXCEÇÃO DELIBERADA AO AZUL DA IDENTIDADE em toda a
 * página, e vêm cravadas do briefing (revisão do cliente, §"Referência de
 * cores das Rodas de Eficiência"). Não são decoração: são o código de cor que
 * identifica cada roda nas mesas, nas plaquinhas e nos materiais impressos do
 * dia — quem chegar no evento vai procurar "a mesa laranja".
 *
 * Por isso elas entram só onde CARREGAM ESSA INFORMAÇÃO (número, ícone, fio
 * da borda e a mesa correspondente na arte) e não no fundo, na tipografia nem
 * em nenhum outro elemento da dobra, que seguem o azul da Rota. O briefing diz
 * exatamente isso: "as cores devem funcionar principalmente como elementos de
 * identificação das quatro experiências, mantendo o restante da dobra dentro
 * da identidade visual da Rota da Eficiência".
 *
 * ⚠️ DOIS VALORES POR RODA, e a razão mudou junto com o fundo da página.
 *
 * Enquanto esta dobra era escura (#0A1628), as cores do briefing passavam
 * cruas como texto: 5,45 / 7,70 / 9,83 / 11,36:1. Sobre BRANCO elas viram
 * 3,33 / 2,35 / 1,85 / 1,60:1 — as quatro reprovam, e o amarelo fica
 * literalmente invisível.
 *
 * Então:
 *   `cor`       o valor exato do briefing. Vive onde é GRAFISMO — as mesas da
 *               planta e o fio da borda. Piso de 3:1, que ele cumpre.
 *   `corTexto`  a mesma matiz rebaixada até passar como texto sobre branco
 *               (6,08 / 5,94 / 6,33 / 6,32:1). Vive no nome do tema e no selo
 *               do número, com branco por cima.
 *
 * O código de cor do briefing continua reconhecível — é a mesma família, um
 * degrau mais escura. Ver o cabeçalho de global.css.
 */
/* ⚠️ COPY REESCRITA EM 12/08 (noite), a partir da transcrição do cliente sobre
   o que acontece em cada mesa. O que havia era uma linha por roda — títulos
   corretos e conteúdo nenhum. Quem lia não sabia o que ia FAZER na mesa, e
   essa é a única dobra da página que responde justamente isso.

   Cada roda passa a ter três campos de texto, e cada um tem um trabalho:
     `chapeu`  a pergunta que a mesa responde, em uma linha
     `texto`   o que acontece nos 30 minutos, em prosa
     `itens`   os assuntos concretos, para quem lê batendo o olho

   ⚠️ NADA AQUI FOI INVENTADO. Cada item sai da transcrição do cliente
   (mercado de locação e de vendas, regras novas e impostos; funil e cadência;
   evento automático na agenda, cadastro de imóvel, cadência do CRM; KPIs no
   Meta, Google Ads e TikTok com o painel do sistema aberto). Onde a
   transcrição não disse, não há linha. */
export const RODAS_APRELAB = [
  {
    tema: 'Mercado e Ecossistema',
    chapeu: 'Para onde o mercado local está indo',
    texto:
      'Meia hora sobre o cenário que decide o seu próximo semestre: como estão o mercado de locação e o de vendas na região, o que as regras novas e os impostos mudam na rotina da imobiliária e onde isso aparece na operação de quem está na mesa.',
    itens: ['Mercado de locação', 'Mercado de vendas', 'Regras novas e impostos'],
    /* A arte de cada roda. `onError` no componente esconde a imagem se o
       arquivo não estiver em disco, então apontar para um caminho que ainda
       não existe não desenha moldura quebrada — a mesa fica com o campo de
       cor e o disco do kit, como antes. */
    imagem: '/img/aprelab-mercado-mesas.webp',
    cor: '#398BFF',
    corTexto: '#0B5BD3',
  },
  {
    tema: 'CRM e Processo Comercial',
    chapeu: 'Funil, cadência e o que trava a conversão',
    texto:
      'Como o funil da imobiliária deveria estar desenhado e qual cadência de atendimento faz o lead andar. Cada participante sai com o próprio processo no papel, do primeiro contato ao fechamento, e com os pontos em que ele trava hoje marcados.',
    itens: ['Funil estruturado', 'Cadência de atendimento', 'Onde a conversão trava'],
    imagem: '/img/aprelab-crm-mesas.webp',
    cor: '#FF8A2B',
    corTexto: '#A34A00',
  },
  {
    tema: 'IA e Automação',
    chapeu: 'O que já dá para tirar da mão do corretor',
    texto:
      'As inteligências artificiais que já trabalham dentro de uma imobiliária hoje, e as rotinas que deixam de depender de alguém lembrar: o evento que entra sozinho na agenda, o imóvel que se cadastra sem digitação, a cadência do CRM que dispara no horário.',
    itens: [
      'Eventos automáticos na agenda',
      'Cadastro de imóvel automático',
      'Automação da cadência do CRM',
    ],
    imagem: '/img/aprelab-ia-mesas.webp',
    cor: '#8BD23C',
    corTexto: '#3E6B12',
  },
  {
    tema: 'Marketing e Geração de Demanda',
    chapeu: 'A vida do lead, do anúncio ao contrato',
    texto:
      'Quais indicadores o mercado imobiliário precisa acompanhar para vender mais, e onde cada um deles aparece no Meta, no Google Ads e no TikTok. Com o painel do nosso sistema aberto na mesa, mostrando os números reais e o que fazer com eles na segunda-feira.',
    itens: [
      'Os KPIs que importam no imobiliário',
      'Meta, Google Ads e TikTok',
      'Painel de dados aberto na mesa',
    ],
    imagem: '/img/aprelab-marketing-mesas.webp',
    cor: '#F1C93B',
    corTexto: '#7A5B00',
  },
]

/**
 * A MECÂNICA DO APRE.LAB, em números — o que o cliente descreveu em 12/08 e
 * que a página nunca tinha dito:
 *
 *   "as pessoas que vão no evento vão ser divididas em quatro grupos e vão
 *    ficar nesse grupo por meia hora em cada um desses assuntos, aprofundando
 *    e discutindo. Então uma rota de conhecimento e crescimento."
 *
 * Ou seja: ninguém escolhe uma mesa. TODO MUNDO PASSA PELAS QUATRO, em rodízio
 * de 30 minutos. Era a informação que faltava para a dobra fazer sentido — sem
 * ela, quatro cartões coloridos leem como quatro opções, e a pessoa fica com a
 * impressão de que vai perder três quartos do conteúdo.
 */
/* NÚMEROS E TEXTOS DITADOS PELO CLIENTE em 13/08 — substituem os de 12/08.
   Mudou o terceiro valor (era `4 mesas para cada grupo`) e as quatro notas.

   ⚠️ OS QUATRO NÚMEROS SÃO UMA CONTA, e ela precisa fechar: 4 grupos × 6 mesas
   por grupo = 24 simultâneas. O cliente ditou "25" no quarto item; avisado da
   inconsistência em 13/08, confirmou 24. Mexer em `grupos` ou em `mesas por
   grupo` sem refazer o quarto número volta a publicar uma contradição na
   mesma tela. */
export const APRELAB_MECANICA = [
  { valor: '4', rotulo: 'grupos', nota: 'Você se conecta com outros players do mercado' },
  { valor: '30', rotulo: 'minutos por mesa', nota: 'Tempo para colocar a mão na massa de verdade' },
  { valor: '6', rotulo: 'mesas por grupo', nota: 'Todos saem com assunto e decisão estratégica para aplicar em sua operação' },
  { valor: '24', rotulo: 'mesas simultâneas', nota: 'Rodadas de muito conhecimento e networking' },
]

/* O fecho da trilha: o que a pessoa leva embora depois de passar pelas quatro
   mesas. Fala de ENTREGA, não de promessa de resultado — "crescimento
   garantido" seria uma garantia que ninguém pode dar, e o briefing do evento
   não a faz em nenhum lugar. */
export const APRELAB_CHEGADA = {
  titulo: 'No fim da rota, você sai com um plano',
  texto:
    'Quatro mesas, quatro assuntos, o dia inteiro em grupo. Quem chega ao fim da rota leva o próprio funil desenhado, a lista do que dá para automatizar já na segunda-feira e os números que precisa acompanhar para vender mais.',
  itens: [
    'O seu processo comercial no papel',
    'O que automatizar primeiro',
    'Os indicadores que movem a receita',
  ],
}

// ── 5ª dobra ────────────────────────────────────────────────────────────────
// O briefing marca estes três selects como "opções padrão a definir por quem
// programa". Definidos aqui; o cliente revisa (pendência 8 do plano).
export const OPCOES_CARGO = [
  'Proprietário / Sócio',
  'Diretor',
  'Gerente',
  'Coordenador',
  'Analista / Corretor',
  'Outro',
]

export const OPCOES_EQUIPE = [
  'Até 5 pessoas',
  'De 6 a 15 pessoas',
  'De 16 a 30 pessoas',
  'De 31 a 50 pessoas',
  'Mais de 50 pessoas',
]

export const OPCOES_LOCACAO = [
  'Sim, é o carro-chefe',
  /* Era "Sim, mas vendas é maior" — sujeito plural com verbo no singular.
     ⚠️ O valor escolhido é gravado LITERALMENTE na coluna `locacao` de
     `inscricoes`, então trocar a string aqui faz o relatório do painel exibir
     duas linhas para a mesma resposta. Em 13/08 havia 2 inscrições com a
     string antiga; normalizá-las é um UPDATE à parte, não um efeito colateral
     de corrigir a página. */
  'Sim, mas vendas são maiores',
  'Ainda não, quero começar',
  'Não trabalho com locação',
]

/* Copy do Apre.Lab — literal da revisão do cliente (PDF do briefing, seção
   "ADICIONAR OUTRA DOBRA ANTES DA INSCRIÇÃO"). Não reescrever sem pedir. */
export const APRELAB_CHAMADA = 'Hora de colocar a mão na massa'
/* ⚠️ REESCRITO EM 12/08 para dizer a MECÂNICA, não o conceito. A frase
   anterior ("o conteúdo dá lugar à prática, em pequenos grupos...") descrevia
   o espírito da coisa e deixava a pergunta prática sem resposta: eu escolho
   uma mesa? fico nela o dia todo? perco as outras três?

   A resposta, na transcrição do cliente: são quatro grupos em rodízio, meia
   hora por mesa, e todo mundo passa pelas quatro. É uma ROTA — o mesmo nome do
   evento, agora com o sentido literal. */
export const APRELAB_SUBTITULO =
  'Você entra em um grupo e percorre as quatro mesas do dia, meia hora em cada uma. ' +
  'Ninguém escolhe um assunto e perde os outros: a rota passa por todos, ' +
  'discutindo a sua operação em cada parada.'
export const APRELAB_DESTAQUE =
  'Aqui, não é só para assistir. Os participantes vão discutir, escrever, comparar ' +
  'processos, identificar gargalos e sair com decisões para levar à imobiliária.'
export const APRELAB_FECHO = 'Problemas reais. Conversas práticas. Próximos passos.'

/* Cardápio do almoço por adesão — pedido do cliente para aparecer na dobra da
   inscrição, junto do campo de interesse. Duas listas porque é assim que o
   cardápio foi entregue (proteínas / acompanhamentos), e porque a pessoa lê
   procurando a proteína primeiro. */
export const CARDAPIO = {
  titulo: 'Cozinha da Kaká',
  grupos: [
    {
      nome: 'Proteínas',
      itens: ['Filé de frango grelhado ao molho de ervas', 'Carne bovina ao molho madeira'],
    },
    {
      nome: 'Acompanhamentos',
      itens: [
        'Arroz branco',
        'Feijão',
        'Legumes salteados na manteiga',
        'Mix de folhas frescas',
        'Farofa artesanal',
        'Batatas rústicas assadas com ervas',
      ],
    },
  ],
}

/* ⚠️ SEM OS VALORES NA FRASE (13/08, pedido do cliente: "a informação que está
   abaixo é repetitiva, deixe mais resumida").

   Ele está certo e o defeito nasceu quando o painel voltou para a lateral do
   formulário: as duas cápsulas de preço ficam DIRETAMENTE ACIMA deste
   parágrafo, então "R$ 59,90 (PIX) ou R$ 64,90 (cartão)" aparecia duas vezes
   em quatro centímetros de tela. O preço mora na cápsula, que é onde o olho
   procura; a frase fica com o que a cápsula não diz — que é opcional, quando
   se escolhe e quando se paga. */
export const TEXTO_ALMOCO =
  'Opcional. Você sinaliza o interesse na inscrição e paga no dia, no local.'

export const TEXTO_LGPD =
  'Aceito receber comunicações da Apresenta.me sobre o evento e autorizo o ' +
  'tratamento dos meus dados para fins de inscrição, conforme a LGPD.'

/**
 * Versão do texto de consentimento, gravada junto de cada inscrição.
 *
 * Guardar só o booleano não demonstra consentimento: sem saber QUAL texto a
 * pessoa aceitou, o registro não prova nada. Ao mudar TEXTO_LGPD, incrementar
 * aqui — senão inscrições novas e antigas ficam indistinguíveis.
 */
export const VERSAO_LGPD = 'v1-2026-08-06'

/**
 * Versão do termo de uso de imagem e voz, gravada junto de cada inscrição —
 * mesmo raciocínio de VERSAO_LGPD acima: o booleano sozinho não demonstra
 * a que texto a pessoa consentiu. Ao alterar o conteúdo de
 * src/pages/termos-de-uso-de-imagem.jsx, incrementar aqui.
 */
export const VERSAO_IMAGEM_VOZ = 'v1-2026-08-11'

// ── 7ª dobra ────────────────────────────────────────────────────────────────
/* Texto REESCRITO pelo cliente na revisão da LP (seção "DOBRA QUEM REALIZA").
   O anterior — "A Apresenta.me surgiu com a missão de simplificar os processos
   imobiliários que são burocráticos…" — vinha do briefing original de 06/08 e
   foi substituído por este; a diferença material é que o novo cita IA e
   automação, e nomeia os módulos um a um. */
export const SOBRE_TEXTO =
  'A Apresenta.me nasceu para simplificar a gestão imobiliária e tornar processos ' +
  'complexos mais eficientes. É uma plataforma completa e integrada que conecta site, ' +
  'CRM, vendas, locação, financeiro, atendimento e gestão em um só lugar. Com ' +
  'inteligência artificial e automação aplicadas à rotina, ajuda a reduzir tarefas ' +
  'manuais, organizar processos e liberar o time para vender e locar muito mais.'

/**
 * Blocos da dobra "quem realiza" — a Apresenta.me como produto, não só como
 * assinatura no rodapé.
 *
 * PROCEDÊNCIA DE CADA COISA, porque isto é material comercial e nada aqui
 * pode ser inventado:
 *   · os módulos e o "atendimento humanizado / equipe especializada /
 *     treinamento personalizado" saíram da própria apresenta.me;
 *   · o implantador dedicado e as reuniões de treinamento vieram do cliente,
 *     na revisão de 11/08;
 *   · o que a apresenta.me NÃO publica — prazo de implantação, horário do
 *     suporte, migração de dados — está fora daqui de propósito. Se algum
 *     desses números for confirmado, ele entra; enquanto não for, não existe.
 */
export const PLATAFORMA = [
  {
    titulo: 'CRM e funil comercial',
    texto: 'Leads centralizados, cadência de atendimento e o histórico de cada negociação num lugar só.',
    icone: 'alvo',
  },
  {
    titulo: 'Locação de ponta a ponta',
    texto: 'Contratos, vistorias, repasses e a rotina do administrador de carteira, sem planilha paralela.',
    icone: 'calculadora',
  },
  {
    titulo: 'Apre.Chat no WhatsApp',
    texto: 'O canal onde o cliente já está, ligado ao CRM: a conversa não morre no celular de um corretor.',
    icone: 'conversa',
  },
  {
    titulo: 'IA e automação na rotina',
    texto: 'Agentes de IA e automações que tiram da mão do time o que é repetitivo e não precisa de gente.',
    icone: 'raio',
  },
  {
    titulo: 'ApBank e financeiro',
    texto: 'Repasses, cobranças e conciliação integrados, com o dinheiro conferido pelo sistema.',
    icone: 'banco',
  },
  {
    titulo: 'Site, BI e relatórios',
    texto: 'Site otimizado para busca e mais de sete relatórios para decidir com dado, não com impressão.',
    icone: 'barras',
  },
]

/**
 * O bloco de "gente" — o que o cliente pediu para ganhar ênfase: suporte
 * humano e o processo de implantação.
 */
export const ACOMPANHAMENTO = {
  chamada: 'Você não fica sozinho depois da assinatura',
  texto:
    'O suporte é um dos nossos maiores diferenciais, e ele é feito por gente: atendimento ' +
    'humanizado, equipe especializada e treinamento no ritmo do seu time.',
  etapas: [
    {
      titulo: 'Implantador dedicado',
      texto: 'Um responsável indicado para conduzir a implantação da sua imobiliária do começo ao fim.',
    },
    {
      titulo: 'Reuniões de treinamento',
      texto: 'Treinamento personalizado com o time, até a operação rodar de verdade no sistema.',
    },
    {
      titulo: 'Suporte humano no WhatsApp',
      texto: 'Atendimento por WhatsApp com equipe especializada, sem fila de robô para falar com alguém.',
    },
  ],
}

export const NUMEROS_APRESENTA = [
  { valor: '+1.000', rotulo: 'Imobiliárias atendidas' },
  { valor: '+12', rotulo: 'Anos de experiência no mercado' },
  { valor: '+7.500', rotulo: 'Usuários ativos' },
]

// ── 8ª dobra ────────────────────────────────────────────────────────────────
// A barra "/" do briefing indica empresas na MESMA cota — por isso `empresas`
// é lista, não string.
// Logos entregues em 11/08 (LAIS, ÓRAGO, CUPOLA) foram convertidas para
// VERSÃO BRANCA monocromática (fills originais — roxo/azul/verde-neon —
// trocados por #FFF): o cartão do patrocinador é escuro, e a diretriz da
// seção sempre foi logo em versão clara (ver o comentário em .cota-empresa,
// componentes.css). Os SVGs originais coloridos não foram mantidos no repo —
// se alguma cota precisar da versão em cor, pedir o arquivo de novo.
export const PATROCINADORES = [
  { cota: 'Ouro',   empresas: [{ nome: 'LAIS', logo: '/img/logo-lais.svg' }] },
  /* JOMANI: vetor TRAÇADO DA ARTE OFICIAL (uploads/jomani.png, 13/08). Entrou
     no lugar da recriação em Montserrat ExtraBold, que o cliente apontou como
     desenho errado — ver o comentário dentro do SVG para o pipeline
     (alfa → threshold → potrace) e para o porquê de vetorizar pelo canal alfa
     e não pela luminância. */
  { cota: 'Prata',  empresas: [{ nome: 'JOMANI', logo: '/img/logo-jomani-oficial.svg' }] },
  /* SCAZA: vetor OFICIAL, entregue já na versão branca da marca (o tique do
     símbolo vem em #F2F2F2, um branco levemente rebaixado — é o desenho de
     dois tons do próprio arquivo, mantido como veio). */
  { cota: 'Bronze', empresas: [{ nome: 'ÓRAGO', logo: '/img/logo-arago.svg' }, { nome: 'SCAZA', logo: '/img/logo-scaza.svg' }] },
  /* QUIRES: vetor TRAÇADO DA ARTE OFICIAL (uploads/quires.png, 13/08). Entrou
     no lugar de `logo-quires-selo.svg`, recriação a mão que o cliente apontou
     como errada — lá o "Q" era um quadrado com anel vazado e cauda horizontal
     e o conjunto lia "VIRES"; no desenho real o "Q" é bloco sólido com
     retângulo vazado e o corte da cauda na quina inferior direita.

     ⚠️ O sufixo no nome do arquivo é CACHE-BUSTING, não capricho: o arquivo em
     `static/` vai ao ar com o nome literal (só o CSS/JS ganham hash no build),
     e o Cloudflare serve com `max-age` de 31 dias. A primeira versão deste
     logo foi publicada como `logo-quires.svg` e ficou presa no cache do edge
     mesmo depois do deploy — a origem já tinha o arquivo novo e o site
     continuava mostrando o velho; daí `-selo`, e agora `-oficial`. Não há
     purge automatizado no publicar-release.js. Portanto: ao SUBSTITUIR
     qualquer imagem já publicada, mudar o nome do arquivo junto. */
  { cota: 'Apoio',  empresas: [{ nome: 'QUIRES', logo: '/img/logo-quires-oficial.svg' }, { nome: 'CUPOLA', logo: '/img/logo-cupola.svg' }] },
]
